// src/features/home/components/TimingReasonDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Chip,
} from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const CONTEXT_META = {
  unscheduled_work: {
    color: 'warning',
    filterType: 'unplannedAttendance',
    filterWork: 'work',
    filterCategories: ['overtime_voluntary', 'overtime_forced', 'unscheduled_workday', 'forskudttid'],
  },
  early_arrival: {
    color: 'warning',
    filterType: 'unplannedAttendance',
    filterWork: 'work',
    // Early arrival = voluntary/forced early overtime or forskudttid
    filterCategories: ['overtime_voluntary', 'overtime_forced', 'forskudttid'],
  },
  late_arrival: {
    color: 'error',
    filterType: 'unplannedAbsence',
    filterWork: 'absence',
    // Late arrival = unplanned absence categories (overslept, personal, etc.)
    filterCategories: [
      'sick_leave_start_reg',
      'care_leave',
      'compassionate_leave',
      'child_care_day',
      'salary_reduction',
    ],
  },
  late_departure: {
    color: 'warning',
    filterType: 'unplannedAttendance',
    filterWork: 'work',
    // Late departure = overtime types
    filterCategories: ['overtime_voluntary', 'overtime_forced', 'overtime_holiday', 'unscheduled_workday'],
  },
  early_departure: {
    color: 'error',
    filterType: 'plannedAbsence',
    filterWork: 'absence',
    filterCategories: [
      'afspadsering',
      'vacation',
      'extra_vacation',
      'fritvalg',
      'care_leave',
      'compassionate_leave',
      'child_care_day',
      'grandchild_care_day',
      'salary_reduction',
    ],
  },
};

/**
 * Prompts the user to select a reason code when clocking in/out outside
 * the planned shift window.
 *
 * Props:
 *   open        — boolean
 *   onClose     — () => void
 *   onConfirm   — (registrationCodeId: string) => void
 *   context     — 'early_arrival' | 'late_arrival' | 'late_departure' | 'early_departure'
 *   codes       — RegistrationCode[] (all department codes)
 *   deviationMinutes — number (how many minutes off)
 */
export default function TimingReasonDialog({
  open,
  onClose,
  onConfirm,
  context,
  codes = [],
  deviationMinutes = 0,
}) {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const [selectedCodeId, setSelectedCodeId] = useState('');

  useEffect(() => {
    if (open) setSelectedCodeId('');
  }, [open]);

  if (!context) return null;

  const meta = CONTEXT_META[context];
  const title = t(`tidsregistering:timing_reason_dialog.contexts.${context}.title`);
  const description = t(`tidsregistering:timing_reason_dialog.contexts.${context}.description`, { minutes: deviationMinutes });
  const filteredCodes = codes.filter(
    (c) =>
      c.registration_type === meta.filterType &&
      c.work_time_type === meta.filterWork &&
      meta.filterCategories.includes(c.registration_category),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberOutlined color={meta.color} />
          {title}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Chip
            label={description}
            color={meta.color}
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          />

          <Typography variant="body2" color="text.secondary">
            {t('tidsregistering:timing_reason_dialog.choose_reason')}
          </Typography>

          {filteredCodes.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              {t('tidsregistering:timing_reason_dialog.no_codes_available')}
            </Typography>
          ) : (
            <RadioGroup
              value={selectedCodeId}
              onChange={(e) => setSelectedCodeId(e.target.value)}
            >
              <Stack spacing={1}>
                {filteredCodes.map((code) => (
                  <Paper
                    key={code.registration_code_id}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      borderColor:
                        selectedCodeId === code.registration_code_id
                          ? 'primary.main'
                          : 'divider',
                      bgcolor:
                        selectedCodeId === code.registration_code_id
                          ? 'primary.50'
                          : 'background.paper',
                    }}
                    onClick={() => setSelectedCodeId(code.registration_code_id)}
                  >
                    <FormControlLabel
                      value={code.registration_code_id}
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {code.title}
                          </Typography>
                          {code.description && (
                            <Typography variant="caption" color="text.secondary">
                              {code.description}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Paper>
                ))}
              </Stack>
            </RadioGroup>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!selectedCodeId}
          onClick={() => onConfirm(selectedCodeId)}
        >
          {t('common:buttons.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
